import { PaletteMode } from '@mui/material';
import { createTheme } from '@mui/material/styles';
// Keep  in order to support Mui DataGrid theme customization
import {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { CSSProperties } from 'react'; // Define custom theme values

import {
  PALETTE_ERROR_MAIN,
  SemanticColorTokens,
  semanticTokensFor,
} from './semanticTokens';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    'form-section-title': CSSProperties;
    'group-title': CSSProperties;
    'nav-group-title': CSSProperties;
    'page-title': CSSProperties;
  }

  interface TypographyVariantsOptions {
    'form-section-title'?: CSSProperties;
    'group-title'?: CSSProperties;
    'nav-group-title'?: CSSProperties;
    'page-title'?: CSSProperties;
  }

  interface Theme {
    brandV2: {
      borderRadius: string;
      colors: {
        treadBlack: string;
        treadBlue: string;
        treadBlue2: string;
        treadGray1: string;
        treadGray2: string;
        treadGray3: string;
        treadGray4: string;
        treadGray5: string;
        treadGray6: string;
        treadGray7: string;
        treadGray8: string;
        treadGray9: string;
        treadGray10: string;
        treadGreen: string;
        treadOrange: string;
        treadOrangeDark: string;
        treadRed: string;
        treadYellow: string;
        treadYellowDark: string;
      };
    };
    semantic: SemanticColorTokens;
  }

  interface ThemeOptions {
    brandV2?: {
      borderRadius?: string;
      colors?: {
        treadBlack?: string;
        treadBlue?: string;
        treadBlue2?: string;
        treadGray1?: string;
        treadGray2?: string;
        treadGray3?: string;
        treadGray4?: string;
        treadGray5?: string;
        treadGray6?: string;
        treadGray7?: string;
        treadGray8?: string;
        treadGray9?: string;
        treadGray10?: string;
        treadGreen?: string;
        treadOrange?: string;
        treadOrangeDark?: string;
        treadRed?: string;
        treadYellow?: string;
        treadYellowDark?: string;
      };
    };
    semantic?: SemanticColorTokens;
  }

  interface Palette {
    brandV2Black: Palette['primary'];
    brandV2Blue2: Palette['primary'];
    brandV2Green: Palette['primary'];
    brandV2Orange: Palette['primary'];
    brandV2OrangeDark: Palette['primary'];
    brandV2Red: Palette['primary'];
    brandV2TreadGray3: Palette['primary'];
    brandV2Yellow: Palette['primary'];
  }

  interface PaletteOptions {
    brandV2Black?: PaletteOptions['primary'];
    brandV2Blue2?: PaletteOptions['primary'];
    brandV2Green?: PaletteOptions['primary'];
    brandV2Orange?: PaletteOptions['primary'];
    brandV2OrangeDark?: PaletteOptions['primary'];
    brandV2Red?: PaletteOptions['primary'];
    brandV2TreadGray3?: PaletteOptions['primary'];
    brandV2Yellow?: PaletteOptions['primary'];
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'form-section-title': true;
    'group-title': true;
    'nav-group-title': true;
    'page-title': true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    brandV2Black: true;
    brandV2Blue2: true;
    brandV2Green: true;
    brandV2Orange: true;
    brandV2OrangeDark: true;
    brandV2Red: true;
    brandV2TreadGray3: true;
    brandV2Yellow: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    brandV2Black: true;
    brandV2Blue2: true;
    brandV2Green: true;
    brandV2Orange: true;
    brandV2OrangeDark: true;
    brandV2Red: true;
    brandV2TreadGray3: true;
    brandV2Yellow: true;
  }
}

// Single source for the brand yellow — it surfaces three ways below
// (palette.primary, brandV2Yellow, brandV2.colors.treadYellow) and they must agree.
const TREAD_YELLOW = '#FFE500';
const TREAD_YELLOW_DARK = '#ECC600';

export const MEDIUM_CHIP_HEIGHT_IN_PX = 26;

export function createAppTheme(mode: PaletteMode) {
  const semantic = semanticTokensFor(mode);
  const isDark = mode === 'dark';

  // The `brandV2.colors` neutral ramp (treadBlack + treadGray1–10) is used ~1,300
  // times across the app as chrome — ink, borders, and surfaces — not as brand
  // marks. The genuine brand hues (yellow/green/orange/red/blue) below stay
  // mode-fixed, but the neutrals MUST follow the mode or the whole app renders
  // light in dark mode. The dark ramp mirrors each light shade's ROLE: the dark
  // inks (treadBlack..treadGray4, used as text/icons) become light inks, and the
  // light surfaces (treadGray7..treadGray10, used as borders/surfaces) become
  // dark ones. Values are aligned to the semantic token layer where the roles
  // coincide (treadBlack≈textPrimary, treadGray3≈textSecondary). See
  // src/theme/semanticTokens.ts.
  //
  // The three surface shades need care, because the role mirror inverts which
  // direction is "up". In light they are wells that recess BELOW the white page;
  // pointing them at the same lightness rank in dark made them rise ABOVE the base
  // instead, landing treadGray8 on semantic.surfaceRaised and treadGray9 on
  // semantic.surface exactly. Every `treadGray8 container + background.paper card`
  // pair then rendered as one flat block (1.00:1) where light shows a step (1.11:1).
  //
  // They now sit on their own rungs of a single monotonic dark ladder:
  //
  //   surfaceSunken / treadGray10   #030C14   deepest app background
  //   surface (base page)           #0A1820   brand deep-black
  //   treadGray9                    #0D1B24   panel / container
  //   treadGray8                    #11202A   hover, selected, border ON that panel
  //   surfaceRaised / paper         #13242E   card, table row, dialog paper
  //   surfaceOverlay                #1B3038   menu, popover
  //
  // treadGray8 and treadGray9 keep separate rungs even though light renders them only
  // 1.02:1 apart, because several screens pair them as background and foreground rather
  // than as two large fills: the Settings nav panel is treadGray9 with the selected route
  // on treadGray8, the agent panel button hovers from one to the other, and the ticket
  // metadata box is treadGray9 with a treadGray8 border. Collapsing them to one value
  // erases those affordances entirely. The pair sits between the base and raised because
  // these shades act both as containers (must sit under cards) and as hover fills on the
  // base (must sit above it).
  const neutralRamp = isDark
    ? {
        treadBlack: '#E6EDF1', // primary ink — matches semantic.textPrimary
        treadGray1: '#D5DDE2', // strong ink
        treadGray2: '#C0C9CF', // ink
        treadGray3: '#A0ABB1', // secondary ink — matches semantic.textSecondary
        treadGray4: '#8B969C', // muted ink / placeholder
        treadGray5: '#79858C', // subtle / disabled ink
        treadGray6: '#4A575E', // subtle divider / strong border
        treadGray7: '#33404A', // border / divider (primary line color)
        treadGray8: '#11202A', // hover / selected / border sitting on a treadGray9 panel
        treadGray9: '#0D1B24', // panel / container — one rung above the base page
        treadGray10: '#030C14', // deepest app background — matches semantic.surfaceSunken
      }
    : {
        treadBlack: '#132732',
        treadGray1: '#334752',
        treadGray2: '#4E606A',
        treadGray3: '#697982',
        treadGray4: '#84929A',
        treadGray5: '#A0ABB1',
        treadGray6: '#BBC4C9',
        treadGray7: '#D6DDE1',
        treadGray8: '#F1F6F9',
        treadGray9: '#F8FBFC',
        treadGray10: '#FAFAFA',
      };

  return createTheme({
    palette: {
      mode,
      // Brand + status colors are mode-fixed; only the neutral chrome
      // (background/divider/action) below branches on mode. The semantic
      // token layer carries the rest.
      primary: {
        main: TREAD_YELLOW,
        dark: TREAD_YELLOW_DARK,
        light: '#FFFDED',
        contrastText: '#1C1C1C',
      },
      secondary: {
        // `color="secondary"` is used across the app as a near-black text/label
        // color (and for Cancel/Edit buttons). #252135 is invisible on a dark
        // surface, so main + contrastText flip in dark mode while light stays
        // pixel-identical. Dark main is a light neutral that reads well both as
        // text and as an (inverted) contained-button surface.
        main: isDark ? '#C0C9CF' : '#252135',
        dark: '#161032',
        light: '#F9F9F9',
        contrastText: isDark ? '#132732' : '#FFFFFF',
      },
      error: {
        main: PALETTE_ERROR_MAIN,
        dark: '#C62828',
        light: '#EF5350',
        contrastText: '#FFFFFF',
      },
      warning: {
        main: '#ED6C02',
        dark: '#E65100',
        light: '#FF9800',
        contrastText: '#FFFFFF',
      },
      info: {
        main: '#0288D1',
        dark: '#01579B',
        light: '#03A9F4',
        contrastText: '#FFFFFF',
      },
      success: {
        main: '#2E7D32',
        dark: '#1B5E20',
        light: '#4CAF50',
        contrastText: '#FFFFFF',
      },
      background: {
        default: semantic.surface,
        paper: semantic.surfaceRaised,
      },
      divider: semantic.divider,
      action: {
        active: isDark ? 'rgba(255, 255, 255, 0.56)' : 'rgba(0, 0, 0, 0.56)',
        hover: semantic.actionHover,
        selected: semantic.actionSelected,
        disabledBackground: semantic.actionDisabledBg,
        focus: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        disabled: semantic.textDisabled,
      },
      brandV2Black: {
        main: '#132732',
        light: '#42525B',
        dark: '#0D1B23',
        contrastText: '#FFFFFF',
      },
      brandV2Blue2: {
        main: '#2196F3',
        light: '#4DABF5',
        dark: '#1769AA',
        contrastText: '#FFFFFF',
      },
      brandV2Green: {
        main: '#00A11A',
        light: '#33B347',
        dark: '#007012',
        contrastText: '#FFFFFF',
      },
      brandV2Orange: {
        main: '#FFAA13',
        light: '#FFBB42',
        dark: '#FF7A00',
        contrastText: '#132732',
      },
      brandV2OrangeDark: {
        main: '#FF7A00',
        light: '#FF9433',
        dark: '#B25500',
        contrastText: 'rgba(0,0,0,.87)',
      },
      brandV2Red: {
        main: '#EA1200',
        light: '#EE4133',
        dark: '#A30C00',
        contrastText: '#FFFFFF',
      },
      brandV2TreadGray3: {
        main: '#697982',
        light: '#a4adb2',
        dark: '#555E63',
        contrastText: '#FFFFFF',
      },
      brandV2Yellow: {
        main: TREAD_YELLOW,
        light: '#FFEA33',
        dark: TREAD_YELLOW_DARK,
        contrastText: '#132732',
      },
    },
    typography: {
      fontFamily: ['Inter', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      fontSize: 14,
    },
    components: {
      MuiSwitch: {
        defaultProps: {
          color: 'primary',
        },
      },

      MuiDataGrid: {
        styleOverrides: {
          columnHeaderTitle: {
            fontSize: '12px',
            fontWeight: '600',
          },
          cell: {
            fontSize: '12px',
            // Restore v6 default vertical centering. v7 dropped the implicit
            // flex/center on cells, so text-only renderCell content top-aligned
            // whenever a sibling column flexed around multi-line content.
            display: 'flex',
            alignItems: 'center',
            // v7 removed the .MuiDataGrid-cellContent style class but existing
            // renderCell usages still set it as a className for ellipsis/overflow.
            // re-add the v6 behavior so those sites continue to truncate instead
            // of overflowing the cell.
            '& .MuiDataGrid-cellContent': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            },
          },
        },
      },

      MuiTypography: {
        defaultProps: {
          variantMapping: {
            'group-title': 'p',
            'nav-group-title': 'p',
            'form-section-title': 'h6',
            'page-title': 'h4',
          },
        },
        variants: [
          {
            props: {
              variant: 'form-section-title',
            },
            style: {
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.6,
            },
          },
          {
            props: {
              variant: 'group-title',
            },
            style: ({ theme }) => ({
              borderBottom: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
              fontSize: 14,
              fontWeight: theme.typography.fontWeightBold,
              marginBottom: theme.spacing(1),
              paddingBottom: theme.spacing(0.5),
            }),
          },
          {
            props: {
              variant: 'nav-group-title',
            },
            style: ({ theme }) => ({
              color: theme.brandV2.colors.treadGray4,
              // 11px reads as a small section label above 13px nav items (Linear-style)
              fontSize: 11,
              fontWeight: theme.typography.fontWeightBold,
              // 0.06em widens the uppercase label so it reads as a header, not a list item
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }),
          },
          {
            props: {
              variant: 'page-title',
            },
            style: {
              fontSize: 32,
              fontWeight: 400,
              lineHeight: 1.235,
            },
          },
        ],
        styleOverrides: {
          root: {
            fontSize: '14px',
          },
          h1: {
            fontSize: 88,
            fontWeight: 200,
          },
          h2: {
            fontSize: 54,
            fontWeight: 200,
          },
          h3: {
            fontSize: 44,
            fontWeight: 300,
          },
          h4: {
            fontSize: 32,
            fontWeight: 400,
          },
          h5: {
            fontSize: 22,
            fontWeight: 400,
          },
          h6: {
            fontSize: 18,
            fontWeight: 600,
          },
          body1: {
            fontSize: 14,
          },
          body2: {
            fontSize: 12,
          },
          overline: {
            fontSize: 10,
            textTransform: 'uppercase',
          },
          subtitle1: {
            fontSize: 14,
            fontWeight: 400,
          },
          subtitle2: {
            fontSize: 12,
            fontWeight: 600,
          },

          caption: {
            fontSize: 10,
            fontWeight: 400,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&:hover': {
              backgroundColor: theme.semantic.listItemHoverBg,
            },
          }),
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            fontSize: '1rem',

            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          },
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
          size: 'small',
        },
        styleOverrides: {
          root: {
            borderRadius: 4,
            textTransform: 'none',
            fontWeight: 400,
            fontSize: '14px',
            fontStyle: 'normal',
            height: '36px',

            '&.Mui-disabled': {
              cursor: 'not-allowed',
            },
          },
          containedPrimary: ({ theme }) => ({
            // Black ink on the brand-yellow button reads in both modes (yellow is
            // mode-fixed). Shadow kept literal — it is elevation, not a surface color.
            color: theme.semantic.textOnBrand,
            boxShadow:
              '0px 3px 1px -2px #00000033; 0px 2px 2px 0px #00000024; 0px 1px 5px 0px #0000001F;',
          }),
        },
      },

      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            marginTop: '2px',
            '& label.Mui-focused': {
              color: theme.semantic.inputFocusLabel,
            },
            '& .MuiFormHelperText-root': {
              '&.Mui-error': {
                margin: '0px',
              },
            },
            '& .MuiOutlinedInput-root': {
              '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                borderWidth: '2px',
              },

              '&:not(.Mui-error).required .MuiOutlinedInput-notchedOutline': {
                border: `2px solid ${theme.palette.primary.main}`,
              },
            },

            '&.required': {
              '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                {
                  border: `2px solid ${theme.palette.primary.main}`,
                },
            },
            '&.prefilled': {
              '& .MuiOutlinedInput-root:not(.Mui-error) .MuiOutlinedInput-notchedOutline':
                {
                  borderStyle: `dashed`,
                },
            },
          }),
        },
      },

      MuiCheckbox: {
        defaultProps: {
          color: 'primary',
        },
      },
      MuiChip: {
        defaultProps: {
          color: 'primary',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'capitalize',
            lineHeight: '18px',
            '&.MuiChip-sizeMedium': {
              height: MEDIUM_CHIP_HEIGHT_IN_PX,
              width: 95,
            },
            '&.MuiChip-outlined': {
              backgroundColor: theme.semantic.chipOutlinedBg,
              '&.MuiChip-colorDefault': {
                backgroundColor: theme.semantic.chipNeutralBg,
                border: `1px solid ${theme.semantic.chipNeutralBorder}`,
                color: theme.semantic.chipNeutralText,
              },
              '&.MuiChip-colorPrimary': {
                backgroundColor: theme.semantic.chipBrandBg,
                border: `1px solid ${theme.semantic.chipBrandBorder}`,
                color: theme.semantic.chipBrandText,
              },
              '&.MuiChip-colorSecondary': {
                backgroundColor: theme.semantic.chipDashedBg,
                border: `1px dashed ${theme.semantic.chipDashedBorder}`,
              },
              '&.MuiChip-colorError': {
                backgroundColor: theme.semantic.statusErrorBg,
                border: `1px solid ${theme.semantic.statusError}`,
                color: theme.semantic.errorText,
              },
              '&.MuiChip-colorInfo': {
                backgroundColor: theme.semantic.statusInfoBg,
                border: `1px solid ${theme.semantic.statusInfo}`,
                color: theme.semantic.statusInfo,
              },
              '&.MuiChip-colorSuccess': {
                backgroundColor: theme.semantic.statusSuccessBg,
                border: `1px solid ${theme.semantic.statusSuccess}`,
                color: theme.semantic.statusSuccess,
              },
              '&.MuiChip-colorWarning': {
                backgroundColor: theme.semantic.statusWarningBg,
                color: theme.semantic.statusWarning,
              },
            },
          }),
        },
      },

      MuiRadio: {
        defaultProps: {
          color: 'primary',
        },
      },

      MuiSlider: {
        defaultProps: {
          color: 'primary',
        },
      },

      MuiInputBase: {
        styleOverrides: {
          root: {
            fontSize: '12px',
          },
        },
      },

      // Every text field, select and autocomplete in the app funnels through
      // MuiOutlinedInput, so this is the one place that decides what an input
      // looks like. Without it the fill is MUI's transparent default and each
      // caller invents its own — five different treatments were in the tree.
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.semantic.surfaceInput,

            '&.Mui-disabled': {
              backgroundColor: theme.semantic.surfaceInputDisabled,
            },
          }),
        },
      },

      MuiSelect: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {},
      },

      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 4,
          },
        },
      },

      MuiDialog: {
        defaultProps: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        styleOverrides: {
          paper: {
            borderRadius: 4,
          },
        },
      },
      //Tabs
      MuiTabs: {
        styleOverrides: {
          root: {
            '.MuiTabs-indicator': {
              height: '2px',
              borderRadius: '6px 6px 0px 0px',
            },
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            fontStyle: 'normal',
            '&.Mui-selected': {
              color: theme.semantic.textStrong,
              '> svg': {
                fill: 'inherit',
              },
            },
          }),
        },
      },
    },

    semantic,

    brandV2: {
      borderRadius: '6px',
      colors: {
        // Neutral ramp follows the active mode (see neutralRamp above).
        ...neutralRamp,
        // Brand hues are mode-fixed — they are the Tread palette, not chrome.
        treadBlue: '#0074DF',
        treadBlue2: '#2196F3',
        treadGreen: '#00A11A',
        treadOrange: '#FFAA13',
        treadOrangeDark: '#FF7A00',
        treadRed: '#EA1200',
        treadYellow: TREAD_YELLOW,
        treadYellowDark: TREAD_YELLOW_DARK,
      },
    },
  });
}

export const lightTheme = createAppTheme('light');
export const darkTheme = createAppTheme('dark');

/**
 * The spacing scale, which is identical in both modes.
 *
 * Module-scope constants and column definitions cannot call `useTheme()`, and reaching
 * for the frozen default export to get spacing drags the whole light-mode palette in
 * with it — where a later `theme.brandV2.colors.*` addition silently renders light on
 * the dark surface. Exposing just the mode-invariant part removes that trap.
 */
export const spacing = lightTheme.spacing;

// Default export is the light theme so existing static `import theme from
// '~theme/AppTheme'` sites keep working unchanged. Components that must react
// to the runtime mode switch should read the theme via `useTheme()` instead.
const theme = lightTheme;

export default theme;
